import { Box, Button, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { APIProvider } from "@vis.gl/react-google-maps";
import { CampaignType, NewCampaignContext } from "contexts/NewCampaign";
import { useContext, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { BentoBrandSearchParameters } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import { API_KEY } from "components/LocationAutocomplete/useAutoCompletePrediction";
import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import BrandSearch from "features/Influencer/Campaigns/NewCampaign/SharedComponents/BrandSearch";
import InfoBox from "features/Influencer/Campaigns/NewCampaign/SharedComponents/InfoBox";
import SelectCollection from "features/Influencer/Campaigns/NewCampaign/SharedComponents/SelectCollection";
import StepperFooter from "features/Influencer/Campaigns/NewCampaign/SharedComponents/StepperFooter";
import { isPage } from "utils/general";

import PreviewCampaignBrands from "../../SharedComponents/PreviewCampaignBrands";
import styles from "./styles";

const CAMPAIGN_TYPES = [
  {
    type: CampaignType.savedList,
    icon: "fa-regular fa-bookmark",
    title: "Saved List",
    description: "Choose from your saved list",
    navigateTo: "collection",
  },
  {
    type: CampaignType.brandSearch,
    icon: "fa-regular fa-magnifying-glass",
    title: "Browse Brands",
    description: "Search and filter all brands",
    navigateTo: "search",
  },
  {
    type: CampaignType.repitching,
    icon: "fa-regular fa-arrows-rotate",
    title: "Repitch Brands",
    description: "Follow up with past contacts",
    navigateTo: "repitch",
  },
];

interface Props {
  handleUpdateCampaign: (body: Map, skipMoveNext?: boolean) => void;
  moveBack: () => void;
  moveNext: () => void;
  submitLoading: boolean;
  errorMessage?: string;
}
export default function CampaignBrands({
  handleUpdateCampaign,
  moveBack,
  moveNext,
  submitLoading,
  errorMessage,
}: Props) {
  const navigate = useNavigate();

  const {
    campaign,
    selectedType,
    setSelectedType,
    selectedBrandIds,
    selectedCollection,
  } = useContext(NewCampaignContext);

  const [isReselecting, setIsReselecting] = useState(false);

  const handleSelect = (type: CampaignType, navigateTo: string) => {
    setSelectedType(type);
    navigate(navigateTo);
  };

  const handleClear = () => {
    if (selectedType) {
      setSelectedType(null);
    } else {
      setSelectedType(null);
      moveBack();
    }
  };

  const handleSubmit = async (
    convertedCurrentSearchParams?: BentoBrandSearchParameters,
    numberSelected?: number,
  ) => {
    if (!isReselecting && campaign?.campaignBrandCount) {
      moveNext();
      return;
    }

    const body: Map = {};
    if (selectedBrandIds.size > 0) {
      body.bentoBrandIds = Array.from(selectedBrandIds);
    }
    if (numberSelected) {
      body.perPage = numberSelected;
    }
    if (
      selectedType === CampaignType.brandSearch ||
      selectedType === CampaignType.repitching
    ) {
      body.searchParameters = convertedCurrentSearchParams;
      body.isRepitching = Boolean(selectedType === CampaignType.repitching);
    } else {
      if (selectedCollection && selectedCollection?.id <= 0) {
        body.isAllSavedCollection = true;
      } else {
        body.savedBrandCollectionId = selectedCollection?.id;
      }
    }
    await handleUpdateCampaign(body, true);
    setSelectedType(null);
    setIsReselecting(false);
  };

  return (
    <>
      {!selectedType && !campaign?.campaignBrandCount && (
        <InfoBox
          icon="fa-regular fa-magnifying-glass"
          title={"Select your target brands"}
          description={"Choose how you want to find brands for this campaign"}
        />
      )}
      {errorMessage && (
        <InfoBox
          icon="fa-regular fa-circle-exclamation"
          description={"Please fix this error to proceed"}
          title={errorMessage}
          error
        />
      )}

      <Box sx={{ mx: { xs: 2.5, md: 4 }, my: 3 }}>
        {!selectedType ? (
          campaign?.campaignBrandCount && !isReselecting ? (
            /* Already selected summary */
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    component="i"
                    className="fa-regular fa-circle-check"
                    sx={{ fontSize: 18, color: "primary.main" }}
                  />
                  <Typography sx={{ fontSize: 14 }}>
                    {campaign.campaignBrandCount} brand
                    {campaign.campaignBrandCount > 1 ? "s" : ""} selected in
                    this campaign
                  </Typography>
                </Box>
                {!isPage(routes.pitchLocal) && (
                  <StyledLargeButton
                    onClick={() => setIsReselecting(true)}
                    sx={{
                      textTransform: "none",
                      flexShrink: 0,
                      ml: 2,
                      color: "purple.dark",
                    }}
                  >
                    Change selection
                  </StyledLargeButton>
                )}
              </Box>

              <PreviewCampaignBrands />
            </Box>
          ) : (
            /* Selection grid */
            <>
              {isReselecting && (
                <Button
                  sx={{
                    color: "purple.dark",
                    textTransform: "none",
                    fontSize: 12,
                    mb: 2,
                  }}
                  onClick={() => setIsReselecting(false)}
                >
                  <Box
                    component={"i"}
                    className="fa-regular fa-arrow-left"
                    sx={{ mr: 1 }}
                  />{" "}
                  Back
                </Button>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                {CAMPAIGN_TYPES.map(
                  ({ type, icon, title, description, navigateTo }) => (
                    <Box
                      key={type}
                      onClick={() => handleSelect(type, navigateTo)}
                      sx={styles.card}
                    >
                      <Box sx={styles.iconContainer}>
                        <Box
                          component="i"
                          className={icon}
                          sx={{ fontSize: 22, color: grey[600] }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                          {title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "text.secondary",
                            mt: 0.5,
                          }}
                        >
                          {description}
                        </Typography>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            </>
          )
        ) : (
          /* Selected state: compact banner + sub-route content */
          <>
            <Button
              onClick={handleClear}
              sx={{
                color: "purple.dark",
                textTransform: "none",
                fontSize: 12,
                mb: 2,
              }}
            >
              <Box
                component="i"
                className="fa-regular fa-arrow-left"
                sx={{ mr: 1 }}
              />{" "}
              Back
            </Button>

            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 2,
              }}
            >
              <Routes>
                <Route
                  path="search"
                  element={
                    <APIProvider apiKey={API_KEY || ""}>
                      <BrandSearch
                        onNext={handleSubmit}
                        nextLoading={submitLoading}
                        onBack={() => setSelectedType(null)}
                      />
                    </APIProvider>
                  }
                />
                <Route
                  path="repitch"
                  element={
                    <APIProvider apiKey={API_KEY || ""}>
                      <BrandSearch
                        repitching
                        onNext={handleSubmit}
                        onBack={() => setSelectedType(null)}
                        nextLoading={submitLoading}
                      />
                    </APIProvider>
                  }
                />
                <Route
                  path="collection"
                  element={
                    <SelectCollection
                      onNext={handleSubmit}
                      onBack={() => setSelectedType(null)}
                      nextLoading={submitLoading}
                    />
                  }
                />
              </Routes>
            </Box>
          </>
        )}
      </Box>

      {!selectedType && (
        <StepperFooter
          handleBack={moveBack}
          handleNext={() => handleSubmit()}
          nextDisabled={Boolean(
            (!selectedType && !campaign?.campaignBrandCount) || errorMessage,
          )}
          nextLoading={submitLoading}
        />
      )}
    </>
  );
}
